import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth as useClerkAuth, useUser } from '@clerk/react';
import api from '../services/api';
import { setGetToken } from '../services/authTokenProvider';
import { safeStorageGet, safeStorageRemove } from '../utils/storage';
import toast from 'react-hot-toast';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { isLoaded: authLoaded, userId, signOut, getToken } = useClerkAuth();
  const { user: clerkUser, isLoaded: userLoaded } = useUser();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authIssue, setAuthIssue] = useState(null);
  const [attemptedUserId, setAttemptedUserId] = useState(null);
  const [lastAuthFailureAt, setLastAuthFailureAt] = useState(0);
  const bootstrapHeaderSentRef = useRef(new Set());
  const signupRoleHeaderEnabled = process.env.REACT_APP_ENABLE_SIGNUP_ROLE_HEADER === 'true';

  const resolveSignupRoleHint = useCallback(() => {
    const roleFromMetadata =
      clerkUser?.publicMetadata?.role ||
      clerkUser?.unsafeMetadata?.role;
    if (roleFromMetadata === 'owner' || roleFromMetadata === 'driver') return roleFromMetadata;

    const roleFromStorage = safeStorageGet('mololine:signup-role-hint');
    if (roleFromStorage === 'owner' || roleFromStorage === 'driver') return roleFromStorage;
    return null;
  }, [clerkUser]);

  const fetchProfile = useCallback(async ({ bootstrap = false } = {}) => {
    if (!userId) {
      setUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const shouldAttachBootstrapHeader =
        signupRoleHeaderEnabled && bootstrap && !bootstrapHeaderSentRef.current.has(userId);
      const signupRoleHint = shouldAttachBootstrapHeader ? resolveSignupRoleHint() : null;
      const reqConfig = signupRoleHint
        ? { headers: { 'X-Signup-Role': signupRoleHint } }
        : undefined;

      const res = await api.get('/accounts/profile/', reqConfig);

      if (shouldAttachBootstrapHeader) {
        bootstrapHeaderSentRef.current.add(userId);
      }

      safeStorageRemove('mololine:signup-role-hint');
      setAuthIssue(null);
      const clerkEmail =
        clerkUser?.primaryEmailAddress?.emailAddress ||
        clerkUser?.emailAddresses?.[0]?.emailAddress ||
        '';
      const clerkName = clerkUser?.fullName || clerkUser?.firstName || '';

      const normalizedName = (res.data?.full_name || '').trim();
      const normalizedEmail = (res.data?.email || '').trim();
      const isPlaceholderName =
        normalizedName.startsWith('clerk-user_') ||
        normalizedName.startsWith('clerk-') ||
        normalizedName === 'Fleet User' ||
        normalizedName === 'Mololine User';
      const isPlaceholderEmail = normalizedEmail.endsWith('@users.invalid');

      setUser({
        ...res.data,
        full_name: isPlaceholderName && clerkName ? clerkName : (res.data?.full_name || clerkName || 'User'),
        email: isPlaceholderEmail && clerkEmail ? clerkEmail : (res.data?.email || clerkEmail || ''),
      });
    } catch (err) {
      setLastAuthFailureAt(Date.now());
      if (err?.response?.status === 401) {
        setAuthIssue(null);
        setUser(null);
      } else if (err?.response?.status === 403) {
        const code = String(err?.response?.data?.code || '').toLowerCase();
        const detail = String(err?.response?.data?.detail || '').toLowerCase();
        if (
          code === 'admin_signup_limit_reached' ||
          detail.includes('maximum number of admin signups reached')
        ) {
          setAuthIssue('admin_signup_limit_reached');
          setUser(null);
          return;
        }

        setAuthIssue('forbidden');
        const clerkEmail =
          clerkUser?.primaryEmailAddress?.emailAddress ||
          clerkUser?.emailAddresses?.[0]?.emailAddress ||
          '';
        const clerkName = clerkUser?.fullName || clerkUser?.firstName || '';

        if (userId) {
          setUser({
            id: userId,
            email: clerkEmail,
            full_name: clerkName || 'Fleet User',
            role: 'owner',
            backend_profile_unavailable: true,
          });
        } else {
          setUser(null);
        }
      } else {
        setAuthIssue(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, clerkUser, resolveSignupRoleHint]);

  useEffect(() => {
    const template =
      process.env.REACT_APP_CLERK_JWT_TEMPLATE ||
      process.env.VITE_CLERK_JWT_TEMPLATE;

    const tokenGetter = template
      ? (opts) => getToken({ ...opts, template })
      : (opts) => getToken(opts);

    setGetToken(tokenGetter);
    return () => setGetToken(null);
  }, [getToken]);

  useEffect(() => {
    if (!authLoaded || !userLoaded) return;

    if (!userId) {
      setUser(null);
      setAuthIssue(null);
      setLoading(false);
      setAttemptedUserId(null);
      return;
    }

    if (attemptedUserId === userId) {
      const now = Date.now();
      if (lastAuthFailureAt && now - lastAuthFailureAt < 8000) {
        setLoading(false);
        return;
      }
      return;
    }

    setAttemptedUserId(userId);
    fetchProfile({ bootstrap: true });
  }, [authLoaded, userLoaded, userId, fetchProfile, attemptedUserId, lastAuthFailureAt]);

  useEffect(() => {
    if (authLoaded && userLoaded) return;

    const timer = setTimeout(() => {
      setLoading(false);
    }, 8000);

    return () => clearTimeout(timer);
  }, [authLoaded, userLoaded]);

  const login = useCallback(async () => {
    throw new Error('Login is managed by Clerk.');
  }, []);

  const logout = useCallback(async () => {
    await signOut({ redirectUrl: '/sign-in' });
    setGetToken(null);
    setUser(null);
    setAuthIssue(null);
    setAttemptedUserId(null);
    setLastAuthFailureAt(0);
    toast.success('Logged out');
  }, [signOut]);

  const register = useCallback(async () => {
    throw new Error('Registration is managed by Clerk.');
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, authIssue, login, logout, register, fetchProfile }}>
      {children}
    </AuthContext.Provider>
  );
}